import React, { useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Dropdown as SemanticDropdown, Label } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import commonPropTypes from '../../../constants/commonPropTypes'
import { formFieldWrapper } from '../formFieldWrapper'
import styles from './styles.scss'
const cx = classnames.bind(styles)

/**
 * Render's labels in a manner that shows e.g. [First One] 4 More... - if there were 5 selected.  The label for the
 * second item is highjacked to just show "4 More..." for example.  The third on are hidden by sending null.
 *
 * @param {bool} isOpen -- true if dropdown is open
 * @param {number} remaining -- count of remaining items selected
 * @param {function} renderLabel -- custom label rendering function
 * @param {object} item -- item to render
 * @param {number} index -- index of item to render
 * @param {object} defaultProps -- sent by semantic dropdown
 * @returns special rendering of label
 */
const renderLabelCondensed = (
  isOpen,
  remaining,
  renderLabel,
  item,
  index,
  defaultProps
) => {
  if (index === 0 || isOpen) {
    return renderLabel
      ? renderLabel(item, index, defaultProps)
      : SemanticDropdown.defaultProps.renderLabel(item, index, defaultProps)
  }

  if (index === 1) {
    return <Label className={cx('more-label')}>{remaining} More...</Label>
  }

  return null
}

const Select = ({
  elmId,
  placeholder,
  fluid,
  label,
  renderLabel,
  condensedWhenClosed,
  onOpen,
  onClose,
  value,
  showError,
  multiple,
  ...rest
}) => {
  const [isOpen, setOpen] = useState(false)

  const onOpenEx = (event, data) => {
    setOpen(true)
    onOpen(event, data)
  }

  const onCloseEx = (event, data) => {
    setOpen(false)
    onClose(event, data)
  }

  const remaining = value?.length > 1 ? value.length - 1 : null

  // If value is undefined and multiple set to true
  // it gives a warning 'Dropdown value must be an array when multiple is set.
  // Received type: Undefined
  if (typeof value === 'undefined' && multiple) {
    value = []
  }

  return (
    <SemanticDropdown
      className={cx({ condensed: !isOpen && condensedWhenClosed && remaining })}
      selection
      data-elm-id={`${elmId}_select`}
      placeholder={placeholder || label}
      fluid={fluid}
      renderLabel={
        condensedWhenClosed
          ? renderLabelCondensed.bind(null, isOpen, remaining, renderLabel)
          : renderLabel
      }
      onOpen={onOpenEx}
      onClose={onCloseEx}
      error={showError}
      multiple={multiple}
      value={value}
      {...rest}
    />
  )
}

Select.defaultProps = {
  fluid: true,
  options: [],
  onOpen: _.noop,
  onClose: _.noop,
}

Select.propTypes = {
  ...commonPropTypes,
  options: PropTypes.array,
  condensedWhenClosed: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
}

export default formFieldWrapper((fieldProps, showError) => (
  <Select {...fieldProps} showError={showError} />
))
